import React, { FunctionComponent, useEffect } from 'react';
import Spinner from 'src/components/Spinner';
import { useGetScreenshotsQuery } from 'src/generated/graphql';
import { useService } from '@xstate/react';
import { GalleryContext, GalleryEvent } from 'src/machines/galleryMachine';
import constants from 'src/constants';
import {
  Count,
  EmptyGalleryRow,
  EmptyImageContainer,
  GalleryContainer,
  ScreenshotsGallery,
  SpinnerContainer,
  Title,
  TitleContainer,
} from './styled';
import GallerySidebarRow from './components/GallerySidebarRow';

const { POLL_RENDERING_IN_STATES, POLL_DELAY } = constants.screenshot;

interface GallerySidebarProps {
  gallerySidebarRef: any;
  captureIsInProgress: boolean;
}

const GallerySidebar: FunctionComponent<GallerySidebarProps> = (props) => {
  const { gallerySidebarRef, captureIsInProgress } = props;
  const [current, send] = useService<GalleryContext, GalleryEvent>(
    gallerySidebarRef,
  );
  const { context } = current;
  const { jobId } = context;

  const { data, stopPolling, startPolling } = useGetScreenshotsQuery({
    variables: { jobId },
    fetchPolicy: 'cache-and-network',
  });

  const hasLoaded = !!data;
  const isRendering = (s: any) =>
    POLL_RENDERING_IN_STATES.includes(s.rendering.state);
  const hasImages = hasLoaded && data!.screenshots!.length > 0;
  const canPoll = hasImages && !captureIsInProgress;
  const shouldPoll = canPoll && data!.screenshots!.some(isRendering);
  const isGalleryBlank = !hasImages;
  const shouldShowSpinner = !hasLoaded;
  const screenshots = data?.screenshots || [];
  const remainingScreenshots = Math.max(0, 10 - screenshots.length);

  useEffect(() => {
    if (shouldPoll) {
      startPolling(POLL_DELAY);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [shouldPoll]);

  return (
    <GalleryContainer>
      <TitleContainer>
        <Title>Gallery</Title>
        {hasLoaded && (
          <Count>{remainingScreenshots} of 10 screenshots remaining</Count>
        )}
      </TitleContainer>

      <ScreenshotsGallery>
        {hasLoaded &&
          hasImages &&
          screenshots.map((screenshot: any) => (
            <GallerySidebarRow
              key={screenshot.id}
              showImage={(id: string, isEnhanced: boolean) =>
                send({ type: 'SHOW_LIGHTBOX', id, isEnhanced })
              }
              deleteScreenshot={(id: string) =>
                send({ type: 'DELETE_SCREENSHOT', id })
              }
              screenshot={screenshot}
            />
          ))}
        {hasLoaded && isGalleryBlank && (
          <EmptyGalleryRow>
            <EmptyImageContainer>
              SD screenshots will appear here
            </EmptyImageContainer>
            <EmptyImageContainer>
              HD screenshots will appear here
            </EmptyImageContainer>
          </EmptyGalleryRow>
        )}
        {shouldShowSpinner && (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )}
      </ScreenshotsGallery>
    </GalleryContainer>
  );
};

export default GallerySidebar;
